import { cn } from "@newxsystems/shared";
import { SproutCardLink } from "../../../../modules/content/components/SproutCardLink/SproutCardLink";
import { SproutCommonContent } from "../../../../modules/content/components/SproutCommonContent/SproutCommonContent";
import { SproutMedia } from "../../../../modules/content/components/SproutMedia/SproutMedia";
import { SproutRichText } from "../../../../modules/content/components/SproutRichText/SproutRichText";
import { SproutSection } from "../../../../modules/content/components/SproutSection/SproutSection";
import type { ITestimonialGridBlockProps } from "./TestimonialGridBlock.props";

const columns = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
} as const;
const spans = {
  1: "col-span-1",
  2: "sm:col-span-2",
  3: "sm:col-span-2 md:col-span-3",
  4: "sm:col-span-2 md:col-span-3 lg:col-span-4",
} as const;

export const TestimonialGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: ITestimonialGridBlockProps) => {
  if (!items?.length) return null;

  return (
    <SproutSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-10 md:gap-14">
        <SproutCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        <div className={cn("grid gap-5", columns[columnCount || 3])}>
          {items.map((item, index) => (
            <SproutCardLink
              className={cn(
                "sprout-paper-lift relative flex min-h-72 flex-col overflow-visible border-navy-ink p-7 text-mineral-gray after:absolute after:-bottom-4 after:left-8 after:size-8 after:rotate-45 after:border-b-2 after:border-r-2 after:border-navy-ink hover:bg-navy-ink hover:after:bg-navy-ink",
                index % 3 === 0 && "bg-charcoal-green-ink after:bg-charcoal-green-ink",
                index % 3 === 1 && "bg-primary after:bg-primary",
                index % 3 === 2 && "bg-navy-ink after:bg-navy-ink",
                spans[item.columnSpan || 1],
              )}
              key={index}
              link={item.cardLink}
            >
              <div className="flex items-center gap-4">
                {item.portrait ? (
                  <SproutMedia
                    aspectRatio="1:1"
                    className="size-14 shrink-0 rounded-full"
                    media={item.portrait.image}
                    sizes="56px"
                  />
                ) : null}
                <div>
                  <p className="font-sprout-mono text-sm font-bold">{item.name}</p>
                  {item.attribution ? (
                    <p className="mt-1 font-sprout-mono text-xs text-mineral-gray/75">
                      {item.attribution}
                    </p>
                  ) : null}
                </div>
              </div>
              <blockquote className="mt-8">
                <SproutRichText
                  className="font-sprout-body text-2xl leading-snug text-mineral-gray md:text-3xl"
                  content={item.quote}
                />
              </blockquote>
            </SproutCardLink>
          ))}
        </div>
      </div>
    </SproutSection>
  );
};

TestimonialGridBlock.displayName = "Block::TestimonialGridBlock";
